import type { HazardsConfig, VehicleConfig } from '../config/hazards.config';
import type { WorldConfig } from '../config/world.config';
import { laneCenterX } from '../level/segments/segmentBuilders';
import { clamp01, DEG_TO_RAD, lerp, smoothstep, TAU } from '../utils/math';
import { HazardKind, VehicleType, type VehicleHazard } from './HazardDefinition';
import { colliderSlot, HazardCue, HazardPhase, setBox, type HazardCollider, type HazardModel, type HazardStepContext } from './HazardModel';

export type VehiclePose = {
  /** Origin in segment space. */
  x: number;
  z: number;
  /** Heading (radians, Babylon yaw: π = facing the runner). */
  yaw: number;
  /** Steering angle of the steered wheels (radians, positive = towards the vehicle's left). */
  steer: number;
  /** Distance driven – the presenter turns it into wheel rotation. */
  travelled: number;
  /** Fork carriage lift (forklift) and mast tilt back (radians). */
  forks: number;
  tilt: number;
};

/** Settings of one vehicle type, including the forklift's fork carriage. */
export type VehicleSettings = VehicleConfig & { readonly forks?: HazardsConfig['forklift']['forks'] };

export function vehicleSettings(config: HazardsConfig, type: VehicleType): VehicleSettings {
  switch (type) {
    case VehicleType.Forklift:
      return config.forklift;
    case VehicleType.CableReel:
      return config.cableReel;
    default:
      return config.truck;
  }
}

/** A zig-zagging vehicle keeps this far off the barrier (m) – it bounces off there. */
const BARRIER_GAP = 0.1;

/** Half the vehicle's width (m) – its widest body part. */
function halfWidth(settings: VehicleSettings): number {
  return Math.max(...settings.body.map((part) => Math.abs(part.center.x) + part.size.x / 2));
}

/**
 * Lateral position of a zig-zagging vehicle (VehicleHazard.zigzag) after `travelled` m of its path: from its start lane
 * diagonally towards one side, bouncing off the barriers – the road crossed every `crossing` m. `slope`: lateral m per
 * m of path (sign = where it heads now), `bounces`: how often it has hit a barrier so far. Pure (also the key-line rules
 * use it).
 */
export function zigzagPose(spec: VehicleHazard, settings: VehicleSettings, world: WorldConfig, travelled: number): { x: number; slope: number; bounces: number } {
  const zigzag = spec.zigzag;
  const x0 = laneCenterX(world, spec.lane);
  if (!zigzag) return { x: x0, slope: 0, bounces: 0 };
  const edge = world.trackWidth / 2 - halfWidth(settings) - BARRIER_GAP;
  const span = 2 * edge;
  const rate = span / zigzag.crossing;
  // unfolded: along a straight line, folded back at every barrier
  const unfolded = x0 + edge + zigzag.toward * rate * travelled;
  const period = 2 * span;
  const m = ((unfolded % period) + period) % period;
  const outward = m <= span;
  return {
    x: -edge + (outward ? m : period - m),
    // the folded line runs the unfolded way while outward, back the other way after a bounce
    slope: zigzag.toward * rate * (outward ? 1 : -1),
    bounces: Math.abs(Math.floor(unfolded / span) - Math.floor((x0 + edge) / span)),
  };
}

/**
 * The crossing length (m of path per crossing of the road) for which a zig-zag starting in `lane` bounces off a barrier
 * exactly when its front meets the runner (`meetZ`) – after `bounces` crossings: even = back at the barrier of its start
 * side, odd = at the other one. There it blocks one outer lane only, the other two stay open (the validator's view).
 */
export function zigzagCrossingAtMeet(lane: number, startZ: number, meetZ: number, bounces: number, settings: VehicleSettings, world: WorldConfig): number {
  const edge = world.trackWidth / 2 - halfWidth(settings) - BARRIER_GAP;
  const span = 2 * edge;
  const atMeet = startZ - (meetZ + settings.front);
  const start = laneCenterX(world, lane) + edge;
  return atMeet / (bounces - start / span);
}

/** Braking never stalls short of the stop position: the vehicle creeps the last centimetres (m/s). */
const CREEP_SPEED = 1e-3;

/** Path distance from the start to where the front meets the runner. */
function meetDistance(spec: VehicleHazard, settings: VehicleSettings): number {
  return spec.startZ - (spec.meetZ + settings.front);
}

/**
 * Forklift / truck driving towards the runner in its lane (slower than the runner – the closing
 * speed is the sum). It waits with the engine running, sounds its horn (warning) and departs so
 * that its front meets the runner exactly at `meetZ` – whatever the run speed. A lane change on
 * the way is announced by a signal and done along a smooth S-curve. It stops at `stopZ`.
 *
 * Colliders: one slot per box of its outline (VehicleConfig.body), front to back – the forklift's fork
 * tines, pallet and carriage ride on the forks.
 */
export class VehicleModel implements HazardModel<VehiclePose> {
  readonly kind = HazardKind.Vehicle;
  phase: HazardPhase = HazardPhase.Idle;
  readonly pose: VehiclePose;
  readonly previous: VehiclePose;
  readonly colliders: readonly HazardCollider[];
  private readonly fromX: number;
  private readonly toX: number;
  private readonly settings: VehicleSettings;
  private speed = 0;
  private phaseTime = 0;
  private signalled = false;
  private bounces = 0;

  /** Seconds the drive to the meeting line takes (acceleration ramp included). */
  static driveTime(spec: VehicleHazard, settings: VehicleSettings): number {
    return meetDistance(spec, settings) / settings.speed + settings.accelerationTime / 2;
  }

  static startLead(spec: VehicleHazard, settings: VehicleSettings): number {
    return settings.telegraph + VehicleModel.driveTime(spec, settings);
  }

  /** Between the lane-change signal and the end of the S-curve (the beacon flashes faster). */
  get signalling(): boolean {
    const change = this.spec.laneChange;
    return this.signalled && change !== undefined && this.pose.z > change.atZ - this.settings.laneChangeLength;
  }

  constructor(
    private readonly spec: VehicleHazard,
    config: HazardsConfig,
    private readonly world: WorldConfig,
  ) {
    this.settings = vehicleSettings(config, spec.vehicle);
    this.colliders = this.settings.body.map(() => colliderSlot());
    this.fromX = laneCenterX(world, spec.lane);
    this.toX = spec.laneChange ? laneCenterX(world, spec.laneChange.toLane) : this.fromX;
    const forks = spec.forks === 'HIGH' ? (this.settings.forks?.highLift ?? 0) : 0;
    const start: VehiclePose = { x: this.fromX, z: spec.startZ, yaw: Math.PI, steer: 0, travelled: 0, forks, tilt: 0 };
    this.pose = { ...start };
    this.previous = { ...start };
    this.updateCollider();
  }

  step(dt: number, ctx: HazardStepContext, cues: HazardCue[]): void {
    Object.assign(this.previous, this.pose);
    const s = this.settings;
    switch (this.phase) {
      case HazardPhase.Idle:
        if (ctx.armed && ctx.eta(this.spec.meetZ) <= VehicleModel.startLead(this.spec, s)) {
          this.phase = HazardPhase.Warning;
          this.phaseTime = 0;
          cues.push(HazardCue.Warning);
        }
        break;
      case HazardPhase.Warning:
        this.phaseTime += dt;
        if (this.phaseTime >= s.telegraph) this.phase = HazardPhase.Active;
        break;
      case HazardPhase.Active:
        this.drive(dt, cues);
        break;
    }
    // Mast tilts back while the forklift carries its load.
    if (s.forks) this.pose.tilt = s.forks.tiltBackDegrees * DEG_TO_RAD * clamp01(this.speed / s.speed);
    this.updateCollider();
  }

  private drive(dt: number, cues: HazardCue[]): void {
    const s = this.settings;
    const stopDistance = this.spec.startZ - this.spec.stopZ;
    const remaining = stopDistance - this.pose.travelled;
    const cruise = Math.min(s.speed, this.speed + (s.speed / s.accelerationTime) * dt);
    // Brakes evenly into the stop position.
    this.speed = remaining <= 0 ? 0 : Math.min(cruise, s.speed * Math.sqrt(clamp01(remaining / s.brakeDistance)) + CREEP_SPEED);
    this.pose.travelled = Math.min(stopDistance, this.pose.travelled + this.speed * dt);
    this.pose.z = this.spec.startZ - this.pose.travelled;

    if (this.spec.zigzag) {
      // zig-zag: diagonally across, bouncing off the barriers (a thud at every bounce)
      const zz = zigzagPose(this.spec, s, this.world, this.pose.travelled);
      if (zz.bounces > this.bounces) cues.push(HazardCue.Impact);
      this.bounces = zz.bounces;
      this.pose.x = zz.x;
      this.pose.yaw = Math.PI - Math.atan(zz.slope);
      return;
    }

    const change = this.spec.laneChange;
    if (!change) return;
    if (!this.signalled && this.pose.z <= change.atZ + s.speed * s.signalLead) {
      this.signalled = true;
      cues.push(HazardCue.Signal);
    }
    // S-curve to the new lane: lateral position along the travelled path.
    const t = clamp01((change.atZ - this.pose.z) / s.laneChangeLength);
    this.pose.x = lerp(this.fromX, this.toX, smoothstep(t));
    const slope = ((this.toX - this.fromX) * 6 * t * (1 - t)) / s.laneChangeLength;
    // Facing −z: a positive lateral drift turns the heading from π towards π/2.
    this.pose.yaw = Math.PI - Math.atan(slope);
    const direction = Math.sign(this.toX - this.fromX);
    this.pose.steer = t > 0 && t < 1 ? direction * s.maxSteerDegrees * DEG_TO_RAD * Math.sin(TAU * t) : 0;
  }

  /** Outline boxes turned with the heading (axis-aligned bounds); carriage parts lifted with the forks, the pallet only when loaded. */
  private updateCollider(): void {
    const cos = Math.cos(this.pose.yaw);
    const sin = Math.sin(this.pose.yaw);
    this.settings.body.forEach((part, i) => {
      const slot = this.colliders[i]!;
      if (part.carriage === 'load' && !this.spec.load) {
        slot.enabled = false;
        return;
      }
      // Vehicle space → segment space for Babylon's yaw: (x, z) → (x cos + z sin, −x sin + z cos).
      const centerX = this.pose.x + part.center.x * cos + part.center.z * sin;
      const centerZ = this.pose.z - part.center.x * sin + part.center.z * cos;
      const halfX = (part.size.x / 2) * Math.abs(cos) + (part.size.z / 2) * Math.abs(sin);
      const halfZ = (part.size.x / 2) * Math.abs(sin) + (part.size.z / 2) * Math.abs(cos);
      const lift = part.carriage ? this.pose.forks : 0;
      // on an upper floor (VehicleHazard.elevation) the whole outline stands on that floor
      const bottom = (this.spec.elevation ?? 0) + lift + part.center.y - part.size.y / 2;
      setBox(slot, centerX - halfX, centerX + halfX, bottom, bottom + part.size.y, centerZ - halfZ, centerZ + halfZ);
    });
  }
}
